import React, { useState, useEffect } from 'react';
import BookingForm from './BookingForm';
import OfferForm from './OfferForm';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function RequestsList({ requests, setRequests, requestsType, equipmentModels, userTelegramId, user, isAuth, onAuthRequired }) {
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedEquipment, setSelectedEquipment] = useState('');
  const [showMyRequests, setShowMyRequests] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRequestType, setSelectedRequestType] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showOfferForm, setShowOfferForm] = useState(false);
  const requestsPerPage = 9;

  const cities = [...new Set(requests.map((request) => request.city))];
  const equipmentTypes = [...new Set(requests.map((request) => equipmentModels[request.equipment_id]))];

  const filteredRequests = requests.filter((request) => {
    const matchesCity = selectedCity ? request.city === selectedCity : true;
    const matchesEquipment = selectedEquipment ? equipmentModels[request.equipment_id] === selectedEquipment : true;
    const matchesMyRequests = showMyRequests ? String(request.user_id) === String(userTelegramId) : true;
    const matchesRequestType = selectedRequestType ? requestsType[request.type_id] === selectedRequestType : true;
    return matchesCity && matchesEquipment && matchesMyRequests && matchesRequestType;
  });

  const indexOfLastRequest = currentPage * requestsPerPage;
  const indexOfFirstRequest = indexOfLastRequest - requestsPerPage;
  const currentRequests = filteredRequests.slice(indexOfFirstRequest, indexOfLastRequest);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const resetFilters = () => {
    setSelectedCity('');
    setSelectedEquipment('');
    setSelectedRequestType('');
    setShowMyRequests(false);
    setCurrentPage(1);
  };

  const deleteRequest = async (requestId) => {
    if (!window.confirm('Удалить заявку?')) return;
    try {
      const response = await fetch(API_URL + '/requests/' + requestId, {
        method: 'DELETE',
        headers: { 'Authorization': 'Bearer ' + localStorage.getItem('token') }
      });
      if (response.ok) {
        setRequests((prev) => prev.filter((r) => r.id !== requestId));
      }
    } catch (error) {
      console.error('Ошибка удаления:', error);
    }
  };

  const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const getTypeName = (id) => requestsType[id] || 'Не указано';
  const getEquipmentName = (id) => equipmentModels[id] || 'Не указано';

  const totalPages = Math.ceil(filteredRequests.length / requestsPerPage);

  return (
    <div className="max-w-7xl mx-auto">
      <h2 className="text-2xl font-black text-slate-900 mb-6 uppercase tracking-tight">Список заявок</h2>

      {/* Фильтры */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 mb-6 flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-2">
          <label className="text-xs font-bold text-slate-500 uppercase">Город:</label>
          <select value={selectedCity} onChange={(e) => setSelectedCity(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-xl text-sm min-w-[140px]">
            <option value="">Все города</option>
            {cities.map((city) => <option key={city} value={city}>{city}</option>)}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-xs font-bold text-slate-500 uppercase">Техника:</label>
          <select value={selectedEquipment} onChange={(e) => setSelectedEquipment(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-xl text-sm min-w-[140px]">
            <option value="">Вся техника</option>
            {equipmentTypes.map((type) => <option key={type} value={type}>{type}</option>)}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-xs font-bold text-slate-500 uppercase">Тип:</label>
          <select value={selectedRequestType} onChange={(e) => setSelectedRequestType(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-xl text-sm min-w-[140px]">
            <option value="">Все типы</option>
            {Object.values(requestsType).map((type) => <option key={type} value={type}>{type}</option>)}
          </select>
        </div>

        <button onClick={resetFilters} className="px-4 py-2 bg-slate-100 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-200 transition cursor-pointer border-none">
          ↻ Сбросить
        </button>

        <button 
          onClick={() => setShowMyRequests(!showMyRequests)}
          className={
            'px-4 py-2 rounded-xl text-sm font-bold transition cursor-pointer border-none ml-auto ' +
            (showMyRequests ? 'bg-gold hover:bg-gold-hover text-industrial-bg shadow-gold' : 'bg-slate-100 text-slate-600 hover:bg-slate-200')
          }
        >
          {showMyRequests ? '✓ Мои заявки' : '📋 Мои заявки'}
        </button>
      </div>

      {/* Сетка карточек 3 колонки */}
      {currentRequests.length === 0 ? (
        <div className="bg-white rounded-2xl border-2 border-dashed border-slate-300 p-12 text-center">
          <div className="text-6xl mb-4">📋</div>
          <h3 className="text-xl font-bold text-slate-800 mb-2">Нет заявок</h3>
          <p className="text-slate-500">Попробуйте изменить фильтры</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {currentRequests.map((request) => {
            const hasPhoto = request.photo_url && request.photo_url.length > 0;
            const isMine = String(request.user_id) === String(userTelegramId);
            
            return (
              <div 
                key={request.id} 
                className={
                  'bg-white rounded-2xl border-2 hover:shadow-lg transition-all overflow-hidden flex flex-col ' +
                  (isMine ? 'border-gold/40' : 'border-slate-200')
                }
              >
                {/* Фото */}
                {hasPhoto && (
                  <div className="h-48 bg-slate-100 overflow-hidden">
                    <img 
                      src={request.photo_url} 
                      alt="Фото техники"
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                )}

                {/* Контент */}
                <div className="p-4 flex-1 flex flex-col">
                  {/* Заголовок */}
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-base font-bold text-slate-900 m-0 line-clamp-2">
                      {getEquipmentName(request.equipment_id)}
                    </h3>
                    <span className={
                      'text-xs font-bold px-2 py-1 rounded whitespace-nowrap ml-2 ' +
                      (request.type_id === 1 ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700')
                    }>
                      {getTypeName(request.type_id)}
                    </span>
                  </div>

                  {/* Поля */}
                  <div className="space-y-2 mb-3 flex-1 text-sm">
                    <div className="flex items-center gap-2 text-slate-600">
                      <span className="text-slate-400">📍</span>
                      <span className="line-clamp-1">{request.city}, {request.address}</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-600">
                      <span className="text-slate-400">📅</span>
                      <span>{formatDate(request.date_requested)}</span>
                    </div>
                    
                    <div className="flex items-center gap-2 text-slate-600">
                      <span className="text-slate-400">👤</span>
                      <span className="line-clamp-1">{request.contact_name || 'Не указано'}</span>
                    </div>
                    {request.phone_visible ? (
                      <div className="flex items-center gap-2 text-emerald-700 bg-emerald-50 px-2 py-1.5 rounded border border-emerald-200">
                        <span>📞</span>
                        <span className="font-medium">{request.phone_number}</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-xs text-slate-500 bg-slate-100 px-2 py-1.5 rounded border border-slate-200">
                        <span>🔒</span>
                        <span>{request.phone_number || '+7 *** *** ** **'} — после брони</span>
                      </div>
                    )}
                  </div>

                  {/* Цена */}
                  {request.price_per_hour && (
                    <div className="bg-gold-50 border border-gold-200 rounded-xl p-2.5 mb-3">
                      <div className="text-xs text-gold-700 font-medium">Цена</div>
                      <div className="text-lg font-black text-gold-600">{request.price_per_hour} ₽/час</div>
                    </div>
                  )}

                  {/* Детали */}
                  {request.details && (
                    <div className="text-xs text-slate-500 mb-3 line-clamp-2">
                      💬 {request.details}
                    </div>
                  )}

                  {/* Кнопки */}
                  <div className="flex gap-2 pt-3 border-t border-slate-100 mt-auto">
                    {isAuth ? (
                      <>
                        {request.type_id === 1 && !isMine && (
                          <button 
                            onClick={() => { setSelectedRequest(request); setShowOfferForm(true); }}
                            className="btn btn-warning flex-1 text-sm"
                          >
                            💰 Предложить
                          </button>
                        )}
                        {request.type_id === 2 && !isMine && (
                          <button 
                            onClick={() => setSelectedRequest(request)} 
                            className="btn btn-primary flex-1 text-sm"
                          >
                            📅 Забронировать
                          </button>
                        )}
                        {isMine && showMyRequests && (
                          <button onClick={() => deleteRequest(request.id)} className="btn flex-1 text-sm bg-red-50 text-red-600 border border-red-200">
                            🗑️ Удалить
                          </button>
                        )}
                        {isMine && !showMyRequests && (
                          <span className="text-xs text-slate-400 py-2">Ваша заявка</span>
                        )}
                      </>
                    ) : (
                      <button onClick={onAuthRequired} className="btn btn-outline flex-1 text-sm">
                        🔐 Войти
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Пагинация */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-8">
          <button 
            onClick={() => paginate(currentPage - 1)} 
            disabled={currentPage === 1}
            className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium disabled:opacity-40 hover:bg-slate-50 transition"
          >
            ← Назад
          </button>
          {Array.from({ length: totalPages }, (_, i) => (
            <button 
              key={i + 1} 
              onClick={() => paginate(i + 1)} 
              className={
                'px-4 py-2 rounded-xl text-sm font-medium transition ' +
                (currentPage === i + 1 ? 'bg-gold text-industrial-bg font-bold' : 'bg-white border border-slate-200 hover:bg-slate-50')
              }
            >
              {i + 1}
            </button>
          ))}
          <button 
            onClick={() => paginate(currentPage + 1)} 
            disabled={currentPage === totalPages}
            className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium disabled:opacity-40 hover:bg-slate-50 transition"
          >
            Вперед →
          </button>
        </div>
      )}

      {/* Формы */}
      {selectedRequest && !showOfferForm && (
        <BookingForm
          request={selectedRequest}
          onClose={() => setSelectedRequest(null)}
          onSuccess={() => setSelectedRequest(null)}
          userTelegramId={userTelegramId}
        />
      )}

      {showOfferForm && selectedRequest && (
        <OfferForm
          request={selectedRequest}
          onClose={() => { setShowOfferForm(false); setSelectedRequest(null); }}
          onSuccess={() => { setShowOfferForm(false); setSelectedRequest(null); alert('Предложение отправлено!'); }}
          userTelegramId={userTelegramId}
        />
      )}
    </div>
  );
}

export default RequestsList;
